import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Eye } from 'lucide-react';
import { labOrderService, labReportService, LabOrder, LabReport } from '../../lib/services.lab';
import toast from 'react-hot-toast';

export default function ReportViewer() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<LabOrder | null>(null);
  const [report, setReport] = useState<LabReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      loadReport();
    }
  }, [orderId]);

  const loadReport = async () => {
    if (!orderId) return;

    try {
      const orderData = await labOrderService.getById(orderId);
      if (!orderData) {
        toast.error('Order not found');
        navigate('/lab');
        return;
      }

      setOrder(orderData);

      const reportData = await labReportService.getByOrder(orderId);
      if (!reportData) {
        toast.error('Report not found');
        navigate('/lab');
        return;
      }

      setReport(reportData);
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (report?.pdf_url) {
      window.open(report.pdf_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center gap-4">
        <button
          onClick={() => navigate('/lab')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lab Report</h1>
          <p className="text-sm text-neutral-600">
            Report #{report?.report_no}
          </p>
        </div>
      </header>

      {/* Report Details */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Patient Information</h3>
            <div className="space-y-1">
              <p className="text-sm">
                <span className="font-medium">Name:</span> {order?.patient?.name}
              </p>
              <p className="text-sm">
                <span className="font-medium">Phone:</span> {order?.patient?.phone}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Report Information</h3>
            <div className="space-y-1">
              <p className="text-sm">
                <span className="font-medium">Report No:</span> {report?.report_no}
              </p>
              <p className="text-sm">
                <span className="font-medium">Finalized:</span>{' '}
                {report?.finalized_at && new Date(report.finalized_at).toLocaleString()}
              </p>
              <p className="text-sm">
                <span className="font-medium">Status:</span>{' '}
                <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  Finalized
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleDownload}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </button>
          <button
            onClick={() => report?.pdf_url && window.open(report.pdf_url, '_blank')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Eye className="w-4 h-4 mr-2" />
            View PDF
          </button>
        </div>
      </div>

      {/* PDF Preview (iframe) */}
      {report?.pdf_url && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4">Report Preview</h3>
          <div className="border rounded-lg overflow-hidden" style={{ height: '800px' }}>
            <iframe
              src={report.pdf_url}
              className="w-full h-full"
              title="Lab Report PDF"
            />
          </div>
        </div>
      )}
    </section>
  );
}
